import { History } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import {
  isRecuratedSince,
  type Product,
} from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';

/**
 * Marks a row whose run processed OLDER data than the live product now holds —
 * `feedProductVersion < version`.
 *
 * This is the entire point of product versioning on this screen: without it the
 * table shows current values while implying they are what the run saw. The
 * comparison needs no extra request, both numbers ride on the listing row.
 *
 * Renders nothing when either number is absent. Version data only appears once
 * the curation service ships versioning and a run uses the wider ingest scope,
 * and "no version information" must never be shown as "up to date" — that would
 * be an assurance we cannot make.
 */
export function RecuratedSinceChip({
  product,
  className,
}: {
  product: Product;
  className?: string;
}) {
  const { t } = useTranslation('catalog-operations');
  if (!isRecuratedSince(product)) return null;

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <span
          data-testid="recurated-since-chip"
          className={cn(
            'bg-warning-50 text-warning inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
            className,
          )}
        >
          <History className="size-3 shrink-0" aria-hidden="true" />
          {t('versions.recuratedSince')}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-64 text-center">
        {t('versions.recuratedSinceHint', {
          runVersion: product.feedProductVersion,
          currentVersion: product.version,
        })}
      </TooltipContent>
    </Tooltip>
  );
}
